/**
 * Result helpers for service functions that talk to the API.
 *
 * Services here have historically been written as `try { return await call() } catch (e) { return e }`.
 * The axios interceptor rejects with a plain `{ statusCode, message }` object, which is truthy — so
 * the near-universal caller check `if (res) { showSuccess(res.message) }` treated every failure as a
 * success and displayed the *server's error text* inside a green success toast. A 409 explaining why
 * a user type could not be deleted was reported as though the deletion had worked.
 *
 * A result is therefore explicit about which it is. `ok`/`fail` never overlap, so a caller cannot
 * accidentally read one as the other.
 */

export const ok = (data) => ({ success: true, data });

export const fail = (error) => ({ success: false, error });

/** Wraps a promise-returning API call into an { success, data | error } result. */
export const attempt = async (call) => {
  try {
    return ok(await call());
  } catch (error) {
    return fail(error);
  }
};

/**
 * Best-effort human-readable message from either half of a result.
 *
 * Failures come through the axios interceptor as `{ statusCode, message }`, but a raw Error (a
 * network drop, a bug in the service) can reach here too, and its `.message` is developer text
 * like "Network Error" — worth showing, but the fallback matters when there is nothing usable.
 */
export const resultMessage = (result, fallback = 'Something went wrong. Please try again.') => {
  if (!result) return fallback;
  const source = result.success ? result.data : result.error;
  const message = source?.message ?? source?.data?.message;
  return typeof message === 'string' && message.trim() ? message.trim() : fallback;
};
